import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE_URL from './config';

function BreedProfile() {
  const { breedCode } = useParams();
  const [breed, setBreed] = useState(null);
  const [stats, setStats] = useState({ purebred: 0, mixed: 0 });
  const [topDogs, setTopDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const breedRes = await fetch(`${API_BASE_URL}/breeds/${breedCode}`);
        if (!breedRes.ok) throw new Error('Breed not found');
        const breedData = await breedRes.json();
        
        console.log('✅ Breed data received:', {
          breedName: breedData.breedName,
          createdBy: breedData.createdBy
        });

        setBreed(breedData);

        const statsRes = await fetch(`${API_BASE_URL}/breeds/${breedCode}/stats`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData.stats || { purebred: 0, mixed: 0 });
          setTopDogs(statsData.topDogs || []);
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [breedCode]);

  if (loading) return <div style={{ padding: '140px 20px', textAlign: 'center' }}>Loading breed profile...</div>;
  if (error) return <div style={{ padding: '140px 20px', color: 'darkred', textAlign: 'center' }}>{error}</div>;
  if (!breed) return <div style={{ padding: '140px 20px', textAlign: 'center' }}>Breed not found.</div>;

  const addedDate = breed.createdAt
    ? new Date(breed.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown';

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '140px 20px 80px', lineHeight: 1.6 }}>

      {/* HEADER */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ color: '#1E88E5', fontSize: '3em', margin: '0 0 8px' }}>
          {breed.breedName}
        </h1>

        <div style={{ fontSize: '1.15em', color: '#666', marginBottom: '10px' }}>
          {breed.breedGroup && <div><strong>Group:</strong> {breed.breedGroup}</div>}
          <div><strong>Creation Status:</strong> {breed.creationStatus}</div>
          <div><strong>Date Added:</strong> {addedDate}</div>
        </div>
      </div>

      {/* IMAGE + BREED OVERVIEW SIDE-BY-SIDE */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '50px', alignItems: 'flex-start', marginBottom: '70px' }}>
        
        {/* LEFT: Image + Creator */}
        <div style={{ flex: '1 1 420px', minWidth: '300px', textAlign: 'center' }}>
          <img
            src={breed.defaultArtUrl}
            alt={breed.breedName}
            style={{
              maxWidth: '100%',
              maxHeight: '520px',
              borderRadius: '16px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.18)',
              objectFit: 'contain'
            }}
          />

          {breed.createdBy && (
            <p style={{ marginTop: '18px', fontSize: '1.05em', color: '#555' }}>
              Created by{' '}
              <span style={{ color: '#1E88E5', fontWeight: 'bold' }}>
                {breed.createdBy}
              </span>
            </p>
          )}
        </div>

        {/* RIGHT: Breed Overview (Description + Brief History) */}
        <div style={{ flex: '2 1 600px', minWidth: '300px' }}>
          <div style={{ 
            background: '#f9fcff', 
            padding: '35px', 
            borderRadius: '14px', 
            border: '1px solid #BBDEFB' 
          }}>
            <h2 style={{ color: '#1976D2', marginBottom: '30px' }}>Breed Overview</h2>

            {/* Brief History */}
            {breed.briefHistory && breed.briefHistory.trim() !== '' && (
              <div style={{ marginBottom: '40px' }}>
                <h3 style={{ 
                  color: '#1976D2', 
                  marginBottom: '14px',
                  fontSize: '1.35em',
                  borderBottom: '2px solid #BBDEFB',
                  paddingBottom: '8px'
                }}>
                  Brief History
                </h3>
                <p style={{ 
                  whiteSpace: 'pre-wrap', 
                  lineHeight: 1.75,
                  fontSize: '1.02em'
                }}>
                  {breed.briefHistory}
                </p>
              </div>
            )}

            {/* Description */}
            {breed.description && breed.description !== 'Description coming soon...' && (
              <div>
                <h3 style={{ 
                  color: '#1976D2', 
                  marginBottom: '14px',
                  fontSize: '1.35em',
                  borderBottom: '2px solid #BBDEFB',
                  paddingBottom: '8px'
                }}>
                  Description
                </h3>
                <p style={{ 
                  whiteSpace: 'pre-wrap', 
                  lineHeight: 1.75,
                  fontSize: '1.02em'
                }}>
                  {breed.description}
                </p>
              </div>
            )}

            {/* Fallback */}
            {(!breed.briefHistory || breed.briefHistory.trim() === '') && 
             breed.description === 'Description coming soon...' && (
              <p style={{ fontStyle: 'italic', color: '#777', textAlign: 'center' }}>
                More information about this breed coming soon...
              </p>
            )}
          </div>
        </div>
      </div>

      {/* FULL-WIDTH BREED STANDARD (now below the flex) */}
      <div style={{ marginBottom: '80px' }}>
        <div style={{ 
          background: '#f9fcff', 
          padding: '35px', 
          borderRadius: '14px', 
          border: '1px solid #BBDEFB' 
        }}>
          <h2 style={{ color: '#1976D2', marginBottom: '30px' }}>Breed Standard</h2>

          {breed.gameStandard && breed.gameStandard.length > 0 ? (
            breed.gameStandard.map((section, index) => (
              <div key={index} style={{ marginBottom: '32px' }}>
                <h3 style={{ 
                  color: '#1976D2', 
                  marginBottom: '12px',
                  fontSize: '1.35em',
                  borderBottom: '2px solid #BBDEFB',
                  paddingBottom: '8px'
                }}>
                  {section.title}
                </h3>
                <p style={{ 
                  whiteSpace: 'pre-wrap', 
                  lineHeight: 1.75,
                  fontSize: '1.02em'
                }}>
                  {section.content}
                </p>
              </div>
            ))
          ) : (
            <p style={{ fontStyle: 'italic', color: '#777' }}>
              Breed standard coming soon...
            </p>
          )}
        </div>
      </div>

      {/* STATS COUNTERS */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', margin: '40px 0 70px', flexWrap: 'wrap' }}>
        <div style={{ background: '#e3f2fd', padding: '14px 28px', borderRadius: '12px', fontSize: '1.15em', textAlign: 'center' }}>
          <strong style={{ fontSize: '1.6em', color: '#1565C0' }}>{stats.purebred}</strong><br />Purebred (100%)
        </div>
        <div style={{ background: '#e3f2fd', padding: '14px 28px', borderRadius: '12px', fontSize: '1.15em', textAlign: 'center' }}>
          <strong style={{ fontSize: '1.6em', color: '#1565C0' }}>{stats.mixed}</strong><br />Mixed / Partial
        </div>
        <div style={{ background: '#e3f2fd', padding: '14px 28px', borderRadius: '12px', fontSize: '1.15em', textAlign: 'center' }}>
          <strong style={{ fontSize: '1.6em', color: '#1565C0' }}>{stats.purebred + stats.mixed}</strong><br />Total Registered
        </div>
      </div>

      {/* TOP DOGS SECTION */}
      <div style={{ marginBottom: '80px' }}>
        <h2 style={{ color: '#1976D2', textAlign: 'center', marginBottom: '30px' }}>
          Most Accomplished {breed.breedName}
        </h2>

        {topDogs.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: topDogs.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(170px, 1fr))', 
            gap: '24px' 
          }}>
            {topDogs.map(dog => (
              <Link key={dog.dogId} to={`/dog/${dog.dogId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={dog.artUrl}
                    alt={dog.registeredName}
                    style={{
                      width: topDogs.length === 1 ? '220px' : '100%',
                      height: topDogs.length === 1 ? '220px' : '170px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      margin: '0 auto'
                    }}
                  />
                  <p style={{ margin: '14px 0 4px', fontWeight: 'bold', fontSize: '1.05em' }}>
                    {dog.registeredName}
                  </p>
                  <small style={{ color: '#555' }}>
                    {dog.earnedTitles?.length || 0} titles • {dog.showRecords?.length || 0} shows
                  </small>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p style={{ textAlign: 'center', fontSize: '1.15em', color: '#555' }}>
            No registered {breed.breedName} yet.
          </p>
        )}
      </div>

      {/* DOWNLOAD BUTTON */}
      {breed.lineartAvailable && breed.lineartFileName && (
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <a
            href={`${API_BASE_URL}/api/files/${encodeURIComponent(breed.lineartFileName)}`}
            download={breed.lineartFileName}
            style={{
              display: 'inline-block',
              padding: '18px 50px',
              background: '#1E88E5',
              color: 'white',
              fontSize: '1.3em',
              fontWeight: 'bold',
              borderRadius: '10px',
              textDecoration: 'none',
              boxShadow: '0 4px 15px rgba(30, 136, 229, 0.3)'
            }}
          >
            📥 Download Official {breed.breedName} Lineart (.PSD)
          </a>
          <p style={{ marginTop: '12px', color: '#555' }}>
            Filename: {breed.lineartFileName}
          </p>
        </div>
      )}

      {/* FINAL CTA */}
      <div style={{ textAlign: 'center' }}>
        <Link
          to="/studbook"
          style={{
            padding: '14px 40px',
            border: '2px solid #1E88E5',
            color: '#1E88E5',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold',
            fontSize: '1.1em'
          }}
        >
          View all {breed.breedName} in the Studbook
        </Link>
      </div>
    </div>
  );
}

export default BreedProfile;